import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Label } from '../../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Plus, Save, Edit2, Trash2, X, FileText, ChevronUp, ChevronDown, Newspaper, Calendar, Eye, EyeOff } from 'lucide-react';
import { ImageUpload } from '../ImageUpload';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const LANGUAGES = [
  { code: 'en', name: 'English', flag: '🇬🇧', bg: 'bg-blue-50', text: 'text-blue-800' },
  { code: 'bg', name: 'Български', flag: '🇧🇬', bg: 'bg-green-50', text: 'text-green-800' },
  { code: 'de', name: 'Deutsch', flag: '🇩🇪', bg: 'bg-yellow-50', text: 'text-yellow-800' }
];

export default function NewsManager() {
  const [articles, setArticles] = useState([]);
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editingArticle, setEditingArticle] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [showPageContent, setShowPageContent] = useState(false);
  const [isSavingContent, setIsSavingContent] = useState(false);
  const [activeLang, setActiveLang] = useState('en');
  
  const [pageContent, setPageContent] = useState({
    hero_subtitle_en: 'NEWS', hero_subtitle_bg: 'НОВИНИ', hero_subtitle_de: 'NACHRICHTEN',
    hero_title_en: 'Latest', hero_title_bg: 'Последни', hero_title_de: 'Aktuelle',
    hero_title_highlight_en: 'Updates', hero_title_highlight_bg: 'новини', hero_title_highlight_de: 'Neuigkeiten',
    hero_description_en: 'Stay informed about company news, industry updates, and transport insights.',
    hero_description_bg: 'Бъдете информирани за фирмени новини, актуализации в индустрията и транспортни прозрения.',
    hero_description_de: 'Bleiben Sie über Unternehmensnachrichten, Branchenupdates und Transporteinblicke informiert.'
  });
  
  const defaultForm = {
    title_en: '', title_bg: '', title_de: '',
    excerpt_en: '', excerpt_bg: '', excerpt_de: '',
    content_en: '', content_bg: '', content_de: '',
    image_url: '', date: new Date().toISOString().split('T')[0],
    category: 'Company News',
    is_featured: false, is_published: true
  };
  const [formData, setFormData] = useState(defaultForm);

  useEffect(() => { fetchArticles(); fetchPageContent(); }, []);

  const fetchPageContent = async () => {
    try {
      const response = await axios.get(`${API}/news-page-content`);
      if (response.data && Object.keys(response.data).length > 0) {
        setPageContent(prev => ({ ...prev, ...response.data }));
      }
    } catch (error) { console.error('Error:', error); }
  };

  const handleSavePageContent = async () => {
    setIsSavingContent(true);
    try {
      await axios.put(`${API}/admin/news-page-content`, pageContent);
      toast.success('Page content saved!');
    } catch (error) { toast.error('Error saving'); }
    finally { setIsSavingContent(false); }
  };

  const fetchArticles = async () => {
    try {
      const response = await axios.get(`${API}/admin/news`);
      setArticles(response.data.articles || []);
    } catch (error) {
      console.error('Error fetching articles:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingArticle) {
        await axios.put(`${API}/admin/news/${editingArticle.id}`, formData);
        toast.success('Article updated!');
      } else {
        await axios.post(`${API}/admin/news`, formData);
        toast.success('Article added!');
      }
      fetchArticles();
      setIsFormOpen(false);
      setEditingArticle(null);
      setFormData(defaultForm);
    } catch (error) {
      toast.error('Error saving article');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this article?')) return;
    try {
      await axios.delete(`${API}/admin/news/${id}`);
      toast.success('Article deleted!');
      fetchArticles();
    } catch (error) {
      toast.error('Error deleting article');
    }
  };

  const startEdit = (article) => {
    setEditingArticle(article);
    setFormData({
      title_en: article.title_en || article.title || '',
      title_bg: article.title_bg || '',
      title_de: article.title_de || '',
      excerpt_en: article.excerpt_en || article.excerpt || '',
      excerpt_bg: article.excerpt_bg || '',
      excerpt_de: article.excerpt_de || '',
      content_en: article.content_en || article.content || '',
      content_bg: article.content_bg || '',
      content_de: article.content_de || '',
      image_url: article.image_url || article.image || '',
      date: article.date ? article.date.split('T')[0] : new Date().toISOString().split('T')[0],
      category: article.category || 'Company News',
      is_featured: article.is_featured === true,
      is_published: article.is_published !== false
    });
    setIsFormOpen(true);
  };

  const updateField = (field, value) => setFormData(prev => ({ ...prev, [field]: value }));
  const updatePageField = (field, value) => setPageContent(prev => ({ ...prev, [field]: value }));

  if (isLoading) return <div className="text-center py-8">Loading...</div>;

  return (
    <div className="space-y-6">
      {/* Page Content Toggle */}
      <Card className="bg-white rounded-3xl shadow-sm">
        <CardContent className="p-4">
          <button onClick={() => setShowPageContent(!showPageContent)} className="w-full flex items-center justify-between text-left">
            <div className="flex items-center gap-2">
              <FileText className="w-5 h-5 text-[#C41E3A]" />
              <span className="font-bold text-slate-900">News Page Content</span>
            </div>
            {showPageContent ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
          </button>
          
          {showPageContent && (
            <div className="mt-4 border-t pt-4">
              <Tabs defaultValue="en" className="w-full">
                <TabsList className="grid w-full grid-cols-3 mb-4">
                  {LANGUAGES.map(lang => (
                    <TabsTrigger key={lang.code} value={lang.code}>{lang.flag} {lang.name}</TabsTrigger>
                  ))}
                </TabsList>
                
                {LANGUAGES.map(lang => (
                  <TabsContent key={lang.code} value={lang.code}>
                    <div className={`p-4 rounded-2xl space-y-3 ${lang.bg}`}>
                      <h4 className={`font-bold ${lang.text}`}>{lang.flag} Hero Section</h4>
                      <div className="grid grid-cols-2 gap-2">
                        <Input value={pageContent[`hero_subtitle_${lang.code}`] || ''} onChange={(e) => updatePageField(`hero_subtitle_${lang.code}`, e.target.value)} placeholder="Subtitle" className="bg-white" />
                        <Input value={pageContent[`hero_title_${lang.code}`] || ''} onChange={(e) => updatePageField(`hero_title_${lang.code}`, e.target.value)} placeholder="Title" className="bg-white" />
                      </div>
                      <Input value={pageContent[`hero_title_highlight_${lang.code}`] || ''} onChange={(e) => updatePageField(`hero_title_highlight_${lang.code}`, e.target.value)} placeholder="Title Highlight" className="bg-white" />
                      <Textarea value={pageContent[`hero_description_${lang.code}`] || ''} onChange={(e) => updatePageField(`hero_description_${lang.code}`, e.target.value)} placeholder="Description" className="bg-white" rows={2} />
                    </div>
                  </TabsContent>
                ))}
              </Tabs>
              <Button onClick={handleSavePageContent} disabled={isSavingContent} className="w-full bg-[#C41E3A] hover:bg-[#A01830] mt-4">
                <Save className="w-4 h-4 mr-2" /> {isSavingContent ? 'Saving...' : 'Save Page Content'}
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold text-slate-900">News Articles</h2>
          <p className="text-sm text-slate-500">Company news and updates</p>
        </div>
        <Button onClick={() => { setIsFormOpen(true); setEditingArticle(null); setFormData(defaultForm); }} className="bg-[#C41E3A] hover:bg-[#A01830] rounded-full">
          <Plus className="w-4 h-4 mr-2" /> Add Article
        </Button>
      </div>

      {/* Form */}
      {isFormOpen && (
        <Card className="mb-6 bg-white rounded-3xl shadow-lg border-0">
          <CardContent className="p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold">{editingArticle ? 'Edit Article' : 'New Article'}</h3>
              <Button variant="ghost" size="sm" onClick={() => setIsFormOpen(false)}><X className="w-4 h-4" /></Button>
            </div>
            
            <form onSubmit={handleSubmit} className="space-y-4">
              {/* Basic Info */}
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <div className="md:col-span-2">
                  <Label>Image</Label>
                  <div className="mt-1">
                    <ImageUpload value={formData.image_url} onChange={(url) => updateField('image_url', url)} />
                  </div>
                </div>
                <div>
                  <Label>Date</Label>
                  <Input type="date" value={formData.date} onChange={(e) => updateField('date', e.target.value)} className="mt-1" />
                </div>
                <div className="flex flex-col gap-2 pt-6">
                  <label className="flex items-center gap-2">
                    <input type="checkbox" checked={formData.is_featured} onChange={(e) => updateField('is_featured', e.target.checked)} />
                    <span className="text-sm">Featured</span>
                  </label>
                  <label className="flex items-center gap-2">
                    <input type="checkbox" checked={formData.is_published} onChange={(e) => updateField('is_published', e.target.checked)} />
                    <span className="text-sm">Published</span>
                  </label>
                </div>
              </div>

              {/* Language Tabs */}
              <Tabs value={activeLang} onValueChange={setActiveLang}>
                <TabsList className="grid w-full grid-cols-3 mb-4">
                  {LANGUAGES.map(lang => (
                    <TabsTrigger key={lang.code} value={lang.code} className="data-[state=active]:bg-slate-800 data-[state=active]:text-white">
                      {lang.flag} {formData[`title_${lang.code}`] && '✓'}
                    </TabsTrigger>
                  ))}
                </TabsList>
                
                {LANGUAGES.map(lang => (
                  <TabsContent key={lang.code} value={lang.code}>
                    <div className={`p-4 rounded-2xl space-y-4 ${lang.bg}`}>
                      <h4 className={`font-bold ${lang.text}`}>{lang.flag} {lang.name}</h4>
                      <div>
                        <Label>Title {lang.code === 'en' && '*'}</Label>
                        <Input value={formData[`title_${lang.code}`]} onChange={(e) => updateField(`title_${lang.code}`, e.target.value)} className="mt-1 bg-white" required={lang.code === 'en'} />
                      </div>
                      <div>
                        <Label>Excerpt (Short summary)</Label>
                        <Textarea value={formData[`excerpt_${lang.code}`]} onChange={(e) => updateField(`excerpt_${lang.code}`, e.target.value)} className="mt-1 bg-white" rows={2} />
                      </div>
                      <div>
                        <Label>Full Content</Label>
                        <Textarea value={formData[`content_${lang.code}`]} onChange={(e) => updateField(`content_${lang.code}`, e.target.value)} className="mt-1 bg-white" rows={6} />
                      </div>
                    </div>
                  </TabsContent>
                ))}
              </Tabs>

              <div className="flex justify-end gap-2">
                <Button type="button" variant="outline" onClick={() => setIsFormOpen(false)}>Cancel</Button>
                <Button type="submit" className="bg-[#C41E3A]"><Save className="w-4 h-4 mr-2" /> Save</Button>
              </div>
            </form>
          </CardContent>
        </Card>
      )}

      {/* Articles List */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {articles.map(article => (
          <Card key={article.id} className={`bg-white rounded-2xl shadow-sm overflow-hidden ${!article.is_published ? 'opacity-60' : ''}`}>
            {(article.image_url || article.image) && (
              <img src={article.image_url || article.image} alt={article.title_en} className="w-full h-40 object-cover" />
            )}
            <CardContent className="p-4">
              <div className="flex items-center gap-2 text-sm text-slate-500 mb-2">
                <Calendar className="w-4 h-4" />
                <span>{article.date?.split('T')[0]}</span>
                {article.is_featured && <span className="bg-yellow-100 text-yellow-700 px-2 py-0.5 rounded-full text-xs font-medium">Featured</span>}
                {!article.is_published && <span className="bg-slate-100 text-slate-500 px-2 py-0.5 rounded-full text-xs">Draft</span>}
              </div>
              <h4 className="font-bold text-slate-900 line-clamp-2">{article.title_en || article.title}</h4>
              <div className="flex gap-1 mt-2">
                {LANGUAGES.map(lang => (
                  <span key={lang.code} className={`text-xs px-1.5 py-0.5 rounded ${article[`title_${lang.code}`] ? lang.bg : 'bg-slate-100 text-slate-400'}`}>{lang.flag}</span>
                ))}
              </div>
              <p className="text-sm text-slate-500 mt-2 line-clamp-2">{article.excerpt_en || article.excerpt}</p>
              <div className="flex gap-2 mt-3 pt-3 border-t">
                <Button variant="outline" size="sm" onClick={() => startEdit(article)} className="flex-1"><Edit2 className="w-4 h-4 mr-1" /> Edit</Button>
                <Button variant="outline" size="sm" onClick={() => handleDelete(article.id)} className="text-red-500"><Trash2 className="w-4 h-4" /></Button>
              </div>
            </CardContent>
          </Card>
        ))}
        {articles.length === 0 && <p className="text-slate-500 text-center py-8 col-span-3">No articles yet.</p>}
      </div>
    </div>
  );
}
